'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';

import { Combobox, type ComboboxOption } from '@/components/ui/combobox/combobox';
import { useIsMobile } from '@/hooks/use-mobile';
import { useVehicleModels } from '../../hooks/use-public-lookups';

/** `entrant_vehicles.model` is varchar(100) — don't offer a value the API will reject. */
const MAX_MODEL_LENGTH = 100;

interface VehicleModelComboboxProps {
  /** Current model value (a catalog model or a custom-typed string). */
  value: string;
  onValueChange: (value: string) => void;
  vehicleType: 'motorcycle' | 'car';
  /** Selected make — drives which catalog models are suggested. */
  make: string;
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  popoverClassName?: string;
}

/**
 * Model selector that suggests catalog models for the chosen make while always
 * allowing a rider to commit their own model if it isn't listed.
 *
 * Catalog models come from `useVehicleModels(type, make)`. We pass `onSearchChange`
 * to capture what the rider types — which disables the Combobox's built-in cmdk
 * filtering, so we filter the options ourselves. A "Use \"<typed>\"" create row
 * lets the rider commit a model that isn't in the catalog: on mobile it leads the
 * list (right under the search box, so free typing is discoverable and Done
 * selects it); on desktop it trails the catalog matches so Enter still
 * autocompletes to the best suggestion. Dismissing the sheet/popover with text
 * typed commits that text too (via `onSearchCommit`) — riders treat the search
 * box as a text field, and losing what they typed was a recurring support
 * complaint. The currently-selected custom value is kept in the options so it
 * renders in the trigger. Mobile (drawer) vs desktop (popover) is handled by
 * Combobox.
 */
export function VehicleModelCombobox({
  value,
  onValueChange,
  vehicleType,
  make,
  disabled,
  placeholder,
  searchPlaceholder,
  className,
  popoverClassName,
}: VehicleModelComboboxProps) {
  const tAuth = useTranslations('auth');
  const isMobile = useIsMobile();
  const [search, setSearch] = useState('');

  const { data: models = [] } = useVehicleModels(vehicleType, make || null);

  // Distinct catalog model names for the selected make.
  const modelNames = useMemo(() => {
    const seen = new Set<string>();
    const names: string[] = [];
    for (const m of models) {
      if (m.model && !seen.has(m.model)) {
        seen.add(m.model);
        names.push(m.model);
      }
    }
    return names;
  }, [models]);

  const query = search.trim();
  const lowerQuery = query.toLowerCase();

  const options = useMemo(() => {
    const result: ComboboxOption[] = [];
    const taken = new Set<string>();

    // "Use \"<typed>\"" create row — commit a model that isn't in the catalog.
    const hasExact =
      value.toLowerCase() === lowerQuery || modelNames.some((m) => m.toLowerCase() === lowerQuery);
    const useRow =
      query && !hasExact && query.length <= MAX_MODEL_LENGTH
        ? { value: query, label: tAuth('vehicleModelUseCustom', { value: query }) }
        : null;

    // Mobile: lead with the create row so it sits right under the search box.
    if (useRow && isMobile) {
      result.push(useRow);
      taken.add(useRow.value);
    }

    // Keep a custom-typed value (not in the catalog) visible & selected in the trigger.
    if (value && !modelNames.includes(value) && !taken.has(value)) {
      result.push({ value, label: value });
      taken.add(value);
    }

    // Catalog suggestions, filtered by the search term (we own filtering because
    // passing onSearchChange disables the Combobox's built-in cmdk filtering).
    for (const name of modelNames) {
      if (taken.has(name)) continue;
      if (query && !name.toLowerCase().includes(lowerQuery)) continue;
      result.push({ value: name, label: name });
      taken.add(name);
    }

    // Desktop: trail with the create row so Enter autocompletes to the best match.
    if (useRow && !isMobile) {
      result.push(useRow);
    }

    return result;
  }, [modelNames, query, lowerQuery, value, isMobile, tAuth]);

  // Dismissed with text typed but nothing tapped — keep what they typed,
  // snapping to the catalog spelling when it matches a suggestion.
  const handleSearchCommit = (typed: string) => {
    if (typed.length > MAX_MODEL_LENGTH) return;
    const catalogMatch = modelNames.find((m) => m.toLowerCase() === typed.toLowerCase());
    onValueChange(catalogMatch ?? typed);
  };

  return (
    <Combobox
      options={options}
      value={value}
      onValueChange={onValueChange}
      onSearchChange={setSearch}
      onSearchCommit={handleSearchCommit}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={tAuth('vehicleModelTypeHint')}
      disabled={disabled}
      className={className}
      popoverClassName={popoverClassName}
      enterKeyHint="done"
    />
  );
}
